/**
 * Controls the contact drawer visibility, view switching (info ↔ form), the
 * message character counter, and in-drawer contact form submission.
 *
 * The Email Us form POSTs natively into a hidden iframe (same pattern as the
 * drop-sign-up banner) so Shopify CAPTCHA works and the parent page — and
 * drawer — never navigate. Success/error are painted at the top of the form.
 *
 * Triggers:
 *   - Any element with class `contact-drawer-toggle`
 *   - Any element with attribute `data-contact-drawer-toggle`
 *   - Any anchor whose href resolves to `#contact` (Shopify menu links can't
 *     carry classes, so the contact menu item still opens the drawer instead of
 *     navigating). Modified/middle clicks fall through so the page stays reachable.
 *
 * Dismisses on the close button, overlay click and Escape. Focus is returned to
 * the trigger on close. On close, the form view is reset back to the info view.
 */
const BODY_CLASS = 'contact-drawer-open';
const TRIGGER_SELECTOR = '.contact-drawer-toggle, [data-contact-drawer-toggle]';
const CONTACT_HASH = '#contact';

function getDrawer() {
  return document.querySelector('[data-contact-drawer]');
}

function isContactHashLink(element) {
  if (!(element instanceof HTMLAnchorElement)) return false;
  const href = element.getAttribute('href');
  if (!href) return false;

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    return url.hash === CONTACT_HASH;
  } catch {
    return false;
  }
}

/** @type {Element | null} */
let lastTrigger = null;

function openDrawer(trigger) {
  const drawer = getDrawer();
  if (!drawer || document.body.classList.contains(BODY_CLASS)) return;

  lastTrigger = trigger instanceof Element ? trigger : null;

  document.body.classList.add(BODY_CLASS);
  document.documentElement.setAttribute('scroll-lock', '');
  drawer.setAttribute('aria-hidden', 'false');

  // Focus the dialog itself — not the first link. Programmatic `.focus()` on a
  // link paints Safari's focus ring (looks like a full-width line under Call us).
  const panel = drawer.querySelector('.contact-drawer__panel');
  if (panel instanceof HTMLElement) panel.focus({ preventScroll: true });
}

function closeDrawer() {
  const drawer = getDrawer();
  if (!drawer || !document.body.classList.contains(BODY_CLASS)) return;

  document.body.classList.remove(BODY_CLASS);
  document.documentElement.removeAttribute('scroll-lock');
  drawer.setAttribute('aria-hidden', 'true');

  switchView(drawer, 'info');

  if (lastTrigger instanceof HTMLElement) {
    lastTrigger.focus();
    lastTrigger = null;
  }
}

function switchView(drawer, slug) {
  if (!slug) return;
  if (slug === 'form') {
    const form = drawer.querySelector('[data-contact-drawer-form]');
    if (form instanceof HTMLFormElement) {
      // Fresh form each time the email view is opened — clear prior
      // success/error UI and restore fields.
      restoreFormUI(form, drawer);
      protectCaptcha(form);
    }
  }
  for (const view of drawer.querySelectorAll('[data-contact-drawer-view]')) {
    const isActive = view instanceof HTMLElement && view.dataset.contactDrawerView === slug;
    view.classList.toggle('is-active', isActive);
    view.toggleAttribute('hidden', !isActive);
  }
  const scroller = drawer.querySelector('.contact-drawer__views');
  if (scroller instanceof HTMLElement) scroller.scrollTop = 0;
  // Same Safari quirk as open — don't focus the back button / first field.
  const panel = drawer.querySelector('.contact-drawer__panel');
  if (panel instanceof HTMLElement) panel.focus({ preventScroll: true });
}

function updateCounter(drawer) {
  const message = drawer.querySelector('[data-contact-drawer-message]');
  const counter = drawer.querySelector('[data-contact-drawer-counter]');
  if (!(message instanceof HTMLTextAreaElement) || !(counter instanceof HTMLElement)) return;
  counter.textContent = String(message.value.length);
}

const SUBMITTING_LABEL = 'Submitting…';
const FALLBACK_ERROR = 'Sorry, something went wrong. Please try again.';

/** True after we kick off a native POST into the hidden iframe. */
let submitPending = false;
/** @type {ReturnType<typeof setTimeout> | null} */
let submitWatchdog = null;

function clearSubmitWatchdog() {
  if (submitWatchdog !== null) {
    clearTimeout(submitWatchdog);
    submitWatchdog = null;
  }
}

function getIframe() {
  return document.querySelector('[data-contact-drawer-iframe]');
}

function getForm() {
  const form = document.querySelector('[data-contact-drawer-form]');
  return form instanceof HTMLFormElement ? form : null;
}

/**
 * Point the contact form at the hidden iframe so a native Shopify POST never
 * navigates the parent page (drawer stays open).
 */
function bindFormTarget(form) {
  const iframe = getIframe();
  if (!(form instanceof HTMLFormElement) || !(iframe instanceof HTMLIFrameElement)) return;
  const name = iframe.getAttribute('name');
  if (name) form.setAttribute('target', name);
  form.setAttribute('action', (form.getAttribute('action') || '/contact').split('#')[0]);
}

function clearAjaxMessage(form) {
  form.querySelectorAll('.contact-drawer__form-message--ajax').forEach((el) => el.remove());
}

function getFieldsEl(form) {
  return form.querySelector('[data-contact-drawer-fields]');
}

function setFieldsVisible(form, visible) {
  const fields = getFieldsEl(form);
  if (fields instanceof HTMLElement) fields.hidden = !visible;
}

/** Reset form UI so it's ready for a new submission. */
function restoreFormUI(form, drawer) {
  clearAjaxMessage(form);
  setFieldsVisible(form, true);
  setFormBusy(form, false);
  const submitBtn = form.querySelector('.contact-drawer__submit');
  if (submitBtn instanceof HTMLButtonElement) {
    submitBtn.textContent = submitBtn.dataset.originalLabel || submitBtn.textContent || 'Submit';
    delete submitBtn.dataset.originalLabel;
  }
  form.reset();
  if (drawer) updateCounter(drawer);
}

function buildFallbackErrorEl(text) {
  const el = document.createElement('p');
  el.className = 'contact-drawer__form-message contact-drawer__form-message--error';
  el.setAttribute('role', 'alert');
  el.textContent = text || FALLBACK_ERROR;
  return el;
}

/**
 * Ensure Shopify hCaptcha is wired before a programmatic submit.
 * @param {HTMLFormElement} form
 * @returns {Promise<void>}
 */
function protectCaptcha(form) {
  return new Promise((resolve) => {
    const captcha = window.Shopify && window.Shopify.captcha;
    if (captcha && typeof captcha.protect === 'function') {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      try {
        captcha.protect(form, done);
        setTimeout(done, 8000);
        return;
      } catch {
        // Fall through — merchant may have CAPTCHA disabled.
      }
    }
    resolve();
  });
}

function setFormBusy(form, busy) {
  form.setAttribute('aria-busy', busy ? 'true' : 'false');
  const submitBtn = form.querySelector('.contact-drawer__submit');
  if (submitBtn instanceof HTMLButtonElement) submitBtn.disabled = busy;
}

function buildSuccessEl(text) {
  const el = document.createElement('div');
  el.className = 'contact-drawer__form-message contact-drawer__form-message--success';
  el.setAttribute('role', 'status');
  const icon = document.createElement('span');
  icon.className = 'contact-drawer__form-message-icon';
  icon.setAttribute('aria-hidden', 'true');
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.5');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M4 12l5 5 11-11');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.appendChild(path);
  icon.appendChild(svg);
  const p = document.createElement('p');
  p.className = 'contact-drawer__form-message-text';
  p.textContent = text;
  el.appendChild(icon);
  el.appendChild(p);
  return el;
}

function readSuccessMessage(form) {
  return form.dataset.successMessage
    || 'Thanks for contacting us. We will get back to you as soon as possible.';
}

/**
 * In-drawer result UI. Drawer never closes/reopens.
 * Success: reset fields, show message at top, keep the form visible.
 * Error: keep fields, show message at top, re-enable form.
 */
function finishSubmit(form, drawer, { posted, messageEl }) {
  if (!messageEl) messageEl = buildFallbackErrorEl();
  messageEl.classList.add('contact-drawer__form-message--ajax');
  clearAjaxMessage(form);
  form.prepend(messageEl);

  const submitBtn = form.querySelector('.contact-drawer__submit');
  if (submitBtn instanceof HTMLButtonElement) {
    submitBtn.textContent = submitBtn.dataset.originalLabel || 'Submit';
    delete submitBtn.dataset.originalLabel;
  }

  setFieldsVisible(form, true);
  setFormBusy(form, false);

  if (posted) {
    form.reset();
    updateCounter(drawer);
  }

  const scroller = drawer.querySelector('.contact-drawer__views');
  if (scroller instanceof HTMLElement) scroller.scrollTop = 0;
  // role=status/alert already announces; avoid focus outline on the card.
}

/**
 * Read the iframe response after a native Shopify contact POST.
 * Same-origin shop responses are readable; CAPTCHA/error pages are too.
 */
function readIframeResult(form) {
  const iframe = getIframe();
  let posted = false;
  let messageEl = null;

  try {
    const doc = iframe && (iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document));
    if (!doc || !doc.body) {
      // Empty/unreadable — treat as success once a real navigation completed
      // (matches drop-sign-up). CAPTCHA error pages are same-origin and readable.
      return { posted: true, messageEl: buildSuccessEl(readSuccessMessage(form)) };
    }

    const bodyText = doc.body.innerText || '';
    let iframePath = '';
    try {
      iframePath = (iframe.contentWindow && iframe.contentWindow.location.pathname) || '';
    } catch {
      iframePath = '';
    }

    const respForm =
      (form.id && doc.getElementById(form.id)) ||
      doc.querySelector('[data-contact-drawer-form]');
    const success = doc.querySelector('[data-contact-drawer-form-posted]')
      || (respForm && respForm.querySelector('.contact-drawer__form-message--success'));
    const error = respForm && respForm.querySelector('.contact-drawer__form-message--error');

    if (success) {
      posted = true;
      messageEl = buildSuccessEl(readSuccessMessage(form));
    } else if (error) {
      messageEl = document.importNode(error, true);
    } else if (
      iframePath.includes('/challenge')
      || /missing captcha|something went wrong/i.test(bodyText)
    ) {
      messageEl = buildFallbackErrorEl(
        /missing captcha/i.test(bodyText)
          ? 'Please try submitting again.'
          : FALLBACK_ERROR
      );
    } else {
      // Shopify success is typically a redirect to a shop page. If our success
      // marker isn't on that template, still treat a completed POST as success.
      posted = true;
      messageEl = buildSuccessEl(readSuccessMessage(form));
    }
  } catch {
    posted = true;
    messageEl = buildSuccessEl(readSuccessMessage(form));
  }

  return { posted, messageEl };
}

function onIframeLoad() {
  if (!submitPending) return;
  submitPending = false;
  clearSubmitWatchdog();

  const form = getForm();
  const drawer = getDrawer();
  if (!form || !drawer) return;

  finishSubmit(form, drawer, readIframeResult(form));
}

/**
 * Native POST into the hidden iframe (after CAPTCHA is ready).
 * Parent page never navigates — drawer stays open.
 */
async function submitContactForm(form) {
  const drawer = getDrawer();
  if (!drawer) return;

  bindFormTarget(form);

  try {
    await protectCaptcha(form);
  } catch {
    // Continue — native submit still the most reliable path.
  }

  submitPending = true;
  clearSubmitWatchdog();
  submitWatchdog = setTimeout(() => {
    if (!submitPending) return;
    submitPending = false;
    submitWatchdog = null;
    finishSubmit(form, drawer, {
      posted: false,
      messageEl: buildFallbackErrorEl(),
    });
  }, 20000);

  // form.submit() bypasses the submit event (no re-entry) and posts into the iframe.
  HTMLFormElement.prototype.submit.call(form);
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const opener = target.closest(TRIGGER_SELECTOR);
  if (opener) {
    event.preventDefault();
    openDrawer(opener);
    return;
  }

  const isPlainClick = !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0;
  if (isPlainClick) {
    const link = target.closest('a');
    if (link && isContactHashLink(link)) {
      event.preventDefault();
      openDrawer(link);
      return;
    }
  }

  if (target.closest('[data-contact-drawer-close]') || target.closest('[data-contact-drawer-overlay]')) {
    event.preventDefault();
    closeDrawer();
    return;
  }

  const drawer = getDrawer();
  if (!drawer) return;

  if (target.closest('[data-contact-drawer-open-form]')) {
    event.preventDefault();
    switchView(drawer, 'form');
    return;
  }

  if (target.closest('[data-contact-drawer-back]')) {
    event.preventDefault();
    switchView(drawer, 'info');
  }
});

document.addEventListener('input', (event) => {
  if (!(event.target instanceof Element)) return;
  if (!event.target.matches('[data-contact-drawer-message]')) return;
  const drawer = getDrawer();
  if (drawer) updateCounter(drawer);
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.matches('[data-contact-drawer-form]')) return;

  // Stop the parent-page navigation. We POST into the hidden iframe instead.
  event.preventDefault();

  if (submitPending || form.getAttribute('aria-busy') === 'true') return;

  clearAjaxMessage(form);
  setFieldsVisible(form, true);

  const submitBtn = form.querySelector('.contact-drawer__submit');
  if (submitBtn instanceof HTMLButtonElement) {
    submitBtn.dataset.originalLabel = submitBtn.textContent || 'Submit';
    submitBtn.textContent = SUBMITTING_LABEL;
  }
  setFormBusy(form, true);

  submitContactForm(form);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && document.body.classList.contains(BODY_CLASS)) {
    event.preventDefault();
    closeDrawer();
  }
});

function bindDrawer() {
  const drawer = getDrawer();
  if (!drawer) return;

  const form = getForm();
  if (form) bindFormTarget(form);

  const iframe = getIframe();
  if (iframe instanceof HTMLIFrameElement && !iframe.dataset.contactDrawerBound) {
    iframe.dataset.contactDrawerBound = 'true';
    iframe.addEventListener('load', onIframeLoad);
  }

  updateCounter(drawer);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindDrawer, { once: true });
} else {
  bindDrawer();
}

// Keep the drawer state fresh when the section is re-rendered by the theme editor.
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target instanceof Element && target.querySelector('[data-contact-drawer]')) {
    bindDrawer();
  }
});

/**
 * Toggle the "live chat online" indicator across the drawer.
 * @param {boolean} isOnline
 */
function setLiveChatStatus(isOnline) {
  const state = isOnline ? 'online' : 'offline';
  const drawer = getDrawer();
  if (!drawer) return;
  for (const dot of drawer.querySelectorAll('.contact-drawer__status-dot')) {
    dot.classList.remove('contact-drawer__status-dot--online', 'contact-drawer__status-dot--offline');
    dot.classList.add(`contact-drawer__status-dot--${state}`);
  }
}

// Public API for the Bambuser (or any) chat integration.
// Usage from the wiring snippet:
//   window.SanneContactDrawer.setLiveChatStatus(count > 0);
window.SanneContactDrawer = window.SanneContactDrawer || {};
window.SanneContactDrawer.setLiveChatStatus = setLiveChatStatus;

/**
 * Attach to a Bambuser embed instance and mirror its `agents-online` event
 * onto the drawer status dot. Safe to call multiple times.
 * @param {{on: Function, getNumberOfOnlineAgents?: Function}} instance
 * @param {string} [queue] Optional queue identifier for the initial query.
 */
function wireBambuser(instance, queue) {
  if (!instance || typeof instance.on !== 'function' || instance.__sanneContactDrawerWired) return;
  instance.__sanneContactDrawerWired = true;

  instance.on('agents-online', (payload) => {
    const count = payload && typeof payload.numberOfAgentsOnline === 'number' ? payload.numberOfAgentsOnline : 0;
    setLiveChatStatus(count > 0);
  });

  // Seed the initial state — the event only fires when the count changes.
  if (typeof instance.getNumberOfOnlineAgents === 'function') {
    Promise.resolve(instance.getNumberOfOnlineAgents(queue))
      .then((res) => setLiveChatStatus((res && res.numberOfAgentsOnline) > 0))
      .catch(() => {});
  }
}

window.SanneContactDrawer.wireBambuser = wireBambuser;

// Auto-wire if the Bambuser embed is already exposed by the theme snippet.
if (window.sanneBambuserEmbed) wireBambuser(window.sanneBambuserEmbed);

// Late-binding hook: any script that boots Bambuser after this module loads
// should dispatch `sanne:bambuser-ready` with the embed instance in `detail`.
document.addEventListener('sanne:bambuser-ready', (event) => {
  const detail = event instanceof CustomEvent ? event.detail : null;
  if (detail) wireBambuser(detail.instance, detail.queue);
});
